import { Button, LoaderIcon } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useElementSize } from '../shared/hooks/useElementSize';

import { ControlPanel } from './components/ControlPannel/ControlPanel';
import { HeatmapActionBar } from './components/HeatmapActionbar/HeatmapActionBar';
import { HeatmapCharts } from './components/HeatmapCharts/HeatmapCharts';
import { SettingsDrawer } from './components/SettingsDrawer/SettingsDrawer';
import styles from './heatmap-widget.module.scss';
import type { HeatmapWidgetState } from './store/HeatmapWidgetState';

export const HeatmapWidget = observer(({ widgetState }: { widgetState: HeatmapWidgetState }) => {
  const { t, ready } = useTranslation('heatmapWidget', { useSuspense: false });
  const bodyRef = useRef<HTMLDivElement>(null);
  const { width } = useElementSize(bodyRef);

  return (
    <div
      ref={bodyRef}
      className={clsx('go-widgets-widget-wrapper', `go-widget-theme-${widgetState.appParams.theme}`, styles.root)}
    >
      {widgetState.isDataLoading || !ready ? (
        <div className={styles.loader}>
          <LoaderIcon size="l" />
        </div>
      ) : (
        <>
          <div className={styles.content}>
            <ControlPanel widgetState={widgetState} width={Math.max(1, width - 31)} />
            {widgetState.errorMessage ? (
              <div role="alert" className={styles.error}>
                <span>{t(widgetState.errorMessage)}</span>
                <Button size="s" onClick={() => widgetState.retry()}>
                  {t('Heatmap-retry')}
                </Button>
              </div>
            ) : (
              <HeatmapCharts widgetState={widgetState} />
            )}
          </div>
          <div
            className={clsx('go-widgets-actions-bar-wrapper', styles.actionsBar, {
              [styles.suppressHover]: widgetState.isSuppressTooltip,
            })}
          >
            <HeatmapActionBar widgetState={widgetState} />
          </div>
          <SettingsDrawer
            settings={widgetState.settings}
            theme={widgetState.appParams.theme}
            isSettingsDrawerOpen={widgetState.isSettingsDrawerOpen}
            setSettingsDrawerOpen={(value) => widgetState.setSettingsDrawerOpen(value)}
          />
        </>
      )}
    </div>
  );
});
